'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { CreditCard, Lock, Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

interface PaymentFormData {
  email: string;
  cardName: string;
  cardNumber: string;
  expiry: string;
  cvv: string;
}

const assignedBlock = 2847294;

export default function PaymentSection() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PaymentFormData>();

  const onSubmit = async (data: PaymentFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/payments/create-stripe-checkout with { email, blockId: assignedBlock, category }
    await new Promise((res) => setTimeout(res, 2200));
    setIsLoading(false);
    toast.success('Block #' + assignedBlock + ' claimed! Your miracle awaits...');
    router.push('/miracle-reveal-screen');
  };

  return (
    <section className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-12">
      <div className="max-w-2xl mx-auto">
        {/* Block assignment card */}
        <div className="cosmic-card rounded-2xl p-6 mb-6 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Your Assigned Block</p>
          <div className="font-display text-5xl font-bold gold-gradient-text metric-number mb-2">
            #{assignedBlock.toLocaleString()}
          </div>
          <p className="text-sm text-muted-foreground">
            This block will be sealed in the MiracleChain forever under your name.
          </p>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="mt-3 text-xs text-accent hover:text-accent/80 flex items-center gap-1 mx-auto transition-colors"
          >
            Block details <ChevronDown size={12} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>
          {showDetails && (
            <div className="mt-4 grid grid-cols-3 gap-3 text-center animate-fade-in">
              {[
                { label: 'Chain Segment', value: 'Segment 285' },
                { label: 'Block Layer', value: 'Layer 7' },
                { label: 'Coordinates', value: '47.5% filled' },
              ].map((item) => (
                <div key={`detail-${item.label}`} className="bg-muted/30 rounded-lg p-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">{item.label}</p>
                  <p className="text-xs font-semibold text-foreground">{item.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment form */}
        <div className="cosmic-card rounded-2xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl font-semibold text-foreground">Complete Your Purchase</h3>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <Sparkles size={14} className="text-primary" />
              <span className="text-sm font-semibold text-primary">$5.00</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="email">
                Email Address
              </label>
              <p className="text-xs text-muted-foreground mb-2">Your miracle confirmation will be sent here</p>
              <input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                placeholder="seeker@miraclechain.io"
                className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              {errors.email && <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>}
            </div>

            {/* Card name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="cardName">
                Name on Card
              </label>
              <input
                id="cardName"
                type="text"
                {...register('cardName', { required: 'Cardholder name is required' })}
                placeholder="Your full name"
                className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all"
              />
              {errors.cardName && <p className="mt-1.5 text-xs text-danger">{errors.cardName.message}</p>}
            </div>

            {/* Card number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="cardNumber">
                Card Number
              </label>
              <div className="relative">
                <input
                  id="cardNumber"
                  type="text"
                  {...register('cardNumber', {
                    required: 'Card number is required',
                    pattern: { value: /^\d{4}\s?\d{4}\s?\d{4}\s?\d{4}$/, message: 'Enter a valid 16-digit card number' },
                  })}
                  placeholder="4242 4242 4242 4242"
                  maxLength={19}
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all font-mono"
                />
                <CreditCard size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
              {errors.cardNumber && <p className="mt-1.5 text-xs text-danger">{errors.cardNumber.message}</p>}
            </div>

            {/* Expiry + CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="expiry">
                  Expiry Date
                </label>
                <input
                  id="expiry"
                  type="text"
                  {...register('expiry', {
                    required: 'Expiry is required',
                    pattern: { value: /^(0[1-9]|1[0-2])\/\d{2}$/, message: 'Format: MM/YY' },
                  })}
                  placeholder="MM/YY"
                  maxLength={5}
                  className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all font-mono"
                />
                {errors.expiry && <p className="mt-1.5 text-xs text-danger">{errors.expiry.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="cvv">
                  CVV
                </label>
                <input
                  id="cvv"
                  type="text"
                  {...register('cvv', {
                    required: 'CVV is required',
                    pattern: { value: /^\d{3,4}$/, message: '3 or 4 digits' },
                  })}
                  placeholder="•••"
                  maxLength={4}
                  className="w-full px-4 py-3 rounded-xl bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-all font-mono"
                />
                {errors.cvv && <p className="mt-1.5 text-xs text-danger">{errors.cvv.message}</p>}
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 rounded-xl font-display font-semibold text-base transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: isLoading
                  ? 'rgba(201,168,76,0.4)'
                  : 'linear-gradient(135deg, #a07830 0%, #f0d080 50%, #c9a84c 100%)',
                color: '#0a0a14',
                boxShadow: isLoading ? 'none' : '0 0 30px rgba(201,168,76,0.4)',
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={18} className="animate-spin" />
                  Sealing Your Block on the Chain...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Sparkles size={18} />
                  Claim Block #{assignedBlock.toLocaleString()} — $5.00
                </span>
              )}
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Lock size={12} />
              <span>256-bit SSL encryption · Powered by Stripe · Instant miracle delivery</span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}